import { NgClass } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from "@angular/router";
import { IconModule, slideDownUp } from "@crm-workspace/common-lib";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { NgScrollbarModule } from "ngx-scrollbar";
import { filter } from "rxjs/operators";
import { HasPermissionDirective } from "../core/directives/has-permission.directive";
import { AuthService } from "../modules/auth/services/auth/auth.service";
import { SessionEntitlementService } from "../core/services/session-entitlement/session-entitlement.service";
import { areAnyPermissionsCoveredByEntitlements } from "../core/config/module-entitlements.config";

@Component({
  selector: "sidebar",
  templateUrl: "./sidebar.html",
  animations: [slideDownUp],
  imports: [
    NgClass,
    RouterLink,
    NgScrollbarModule,
    RouterLinkActive,
    TranslateModule,
    IconModule,
    HasPermissionDirective,
  ],
})
export class SidebarComponent implements OnInit {
  private static readonly ROUTE_ACCORDION_PREFIXES: ReadonlyArray<{
    prefix: string;
    accordion: string;
  }> = [
    { prefix: "/projects", accordion: "project-management" },
    { prefix: "/campaigns", accordion: "pre-sale" },
    { prefix: "/social-hub", accordion: "pre-sale" },
    { prefix: "/formiq", accordion: "pre-sale" },
    { prefix: "/leads", accordion: "pre-sale" },
    { prefix: "/people", accordion: "pre-sale" },
    { prefix: "/opportunities", accordion: "pre-sale" },
    { prefix: "/my-quotations", accordion: "pre-sale" },
    { prefix: "/site-visits", accordion: "pre-sale" },
    { prefix: "/calendar-events", accordion: "pre-sale" },
    { prefix: "/property-bookings", accordion: "property-bookings" },
    { prefix: "/template-config", accordion: "templates-config" },
    { prefix: "/bank", accordion: "bank-management" },
    { prefix: "/activities", accordion: "activities" },
    { prefix: "/user", accordion: "user-management" },
    { prefix: "/rules-and-automations", accordion: "rules-and-automations" },
    { prefix: "/price-management", accordion: "price-management" },
    { prefix: "/integrations", accordion: "integrations" },
    { prefix: "/social-hub", accordion: "integrations" },
    { prefix: "/settings", accordion: "system" },
    { prefix: "/system", accordion: "system" },
  ];

  active = false;
  store: any;
  activeDropdown: string[] = [];
  parentDropdown: string = "";
  private readonly destroyRef = inject(DestroyRef);
  private readonly authService = inject(AuthService);
  private readonly sessionEntitlements = inject(SessionEntitlementService);

  constructor(
    public translate: TranslateService,
    public storeData: Store<any>,
    public router: Router
  ) {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.syncAccordionFromUrl());
  }

  get isNavLocked(): boolean {
    return !this.authService.hasFullPortalAccess();
  }

  get canSeeBuildersNav(): boolean {
    return this.canSeeModule("builders", ["VIEW_BUILDERS"]);
  }

  get canSeeDashboardNav(): boolean {
    return this.canSeeModule("dashboard", ["VIEW_DASHBOARD"]);
  }

  get canSeeProjectsNav(): boolean {
    return this.canSeeModule("projects", ["VIEW_MY_PROJECTS", "VIEW_PROJECTS"]);
  }

  /** Builder-scoped payment schedule (same RBAC slug as Admin portal). */
  get canSeePaymentScheduleNav(): boolean {
    return this.canSeeModule("projects", [
      "VIEW_PROJECTS",
      "project_payment_schedule.view",
    ]);
  }

  get canSeePriceManagementNav(): boolean {
    return this.canSeeModule("price-management", [
      "VIEW_CP_PRICE_MANAGEMENT",
      "MANAGE_CP_PRICE_MANAGEMENT",
    ]);
  }

  isProjectNavActive(): boolean {
    const path = this.router.url.split("?")[0];
    if (
      path === "/projects/documents" ||
      path.startsWith("/projects/documents/") ||
      path.startsWith("/projects/project-document") ||
      path === "/projects/work-progress" ||
      path.startsWith("/projects/work-progress/") ||
      path === "/projects/payment-schedule" ||
      path.startsWith("/projects/payment-schedule/")
    ) {
      return false;
    }
    return path === "/projects" || path.startsWith("/projects/");
  }

  isProjectDocumentNavActive(): boolean {
    const path = this.router.url.split("?")[0];
    return (
      path === "/projects/documents" ||
      path.startsWith("/projects/documents/") ||
      path.startsWith("/projects/project-document")
    );
  }

  isWorkProgressNavActive(): boolean {
    const path = this.router.url.split("?")[0];
    return (
      path === "/projects/work-progress" ||
      path.startsWith("/projects/work-progress/")
    );
  }

  isPaymentScheduleNavActive(): boolean {
    const path = this.router.url.split("?")[0];
    return (
      path === "/projects/payment-schedule" ||
      path.startsWith("/projects/payment-schedule/")
    );
  }

  /** Builder-connected CP only (hidden for standalone org logins). */
  private get canSeeBuilderCommercialNav(): boolean {
    return this.authService.hasApprovedBuilderAccess();
  }

  get canSeeOffersNav(): boolean {
    return (
      this.canSeeBuilderCommercialNav &&
      this.canSeeModule("offers", ["VIEW_OFFERS"])
    );
  }

  get canSeeIncentiveReportsNav(): boolean {
    return (
      this.canSeeBuilderCommercialNav &&
      this.canSeeModule("incentive-reports", ["VIEW_INCENTIVE_REPORTS"])
    );
  }

  get canSeeInvoicesNav(): boolean {
    return (
      this.canSeeBuilderCommercialNav &&
      this.canSeeModule("invoices", ["VIEW_INVOICES"])
    );
  }

  get canSeeTemplateConfigNav(): boolean {
    return this.canSeeModule("templates-config", [
      "VIEW_CP_DOCUMENT_TEMPLATES",
      "MANAGE_CP_DOCUMENT_TEMPLATES",
      "VIEW_CP_EMAIL_TEMPLATES",
      "MANAGE_CP_EMAIL_TEMPLATES",
      "VIEW_CP_WHATSAPP_TEMPLATES",
      "MANAGE_CP_WHATSAPP_TEMPLATES",
    ]);
  }

  get canSeeBankApfNav(): boolean {
    return this.canSeeModule("bank", ["bank_apf.view", "bank_apf.manage"]);
  }

  get canSeeBankOffersNav(): boolean {
    return this.canSeeModule("bank", ["bank_offers.view", "bank_offers.manage"]);
  }

  get canSeeBankFeesNav(): boolean {
    return this.canSeeModule("bank", ["bank_fees.view", "bank_fees.manage"]);
  }

  get canSeeBankManagementNav(): boolean {
    return this.canSeeBankApfNav || this.canSeeBankOffersNav || this.canSeeBankFeesNav;
  }

  get canSeePaymentsNav(): boolean {
    return (
      this.canSeeBuilderCommercialNav &&
      this.canSeeModule("payments", ["VIEW_PAYMENTS"])
    );
  }

  get canSeeApprovalManagementNav(): boolean {
    return this.canSeeModule("approval-management", [
      "VIEW_APPROVAL_MANAGEMENT",
      "MANAGE_USERS",
    ]);
  }

  get canSeePreSaleNav(): boolean {
    return (
      this.canSeeModule("campaigns", ["VIEW_CAMPAIGNS"]) ||
      this.canSeeModule("social_hub", ["VIEW_SOCIAL_HUB"]) ||
      this.canSeeModule("leads", ["VIEW_LEADS"]) ||
      this.canSeeModule("opportunities", ["VIEW_OPPORTUNITIES"]) ||
      this.canSeeModule("calendar-events", ["VIEW_MEETINGS", "VIEW_LEADS"]) ||
      this.canSeeModule("site-visits", ["VIEW_SITE_VISITS"]) ||
      this.canSeeModule("formiq", ["VIEW_FORMIQ"])
    );
  }

  /**
   * Parent "Property Bookings" group. Broadened beyond VIEW_MY_PROJECT_BOOKINGS so a
   * partner granted only demand-payments or payment-transactions still gets the group
   * rendered — otherwise their route would work but have no nav entry.
   */
  get canSeePropertyBookingsNav(): boolean {
    return this.canSeeModule("bookings", [
      "VIEW_MY_PROJECT_BOOKINGS",
      "VIEW_CP_DEMAND_PAYMENTS",
      "VIEW_CP_PAYMENT_TRANSACTIONS",
    ]);
  }

  get canSeeDemandPaymentsNav(): boolean {
    return this.canSeeModule("bookings", ["VIEW_CP_DEMAND_PAYMENTS"]);
  }

  get canSeePaymentTransactionsNav(): boolean {
    return this.canSeeModule("bookings", ["VIEW_CP_PAYMENT_TRANSACTIONS"]);
  }

  get canSeeActivitiesNav(): boolean {
    return this.canSeeModule("activities", [
      "VIEW_LEADS",
      "VIEW_OPPORTUNITIES",
    ]);
  }

  get canSeeUserManagementNav(): boolean {
    return this.canSeeModule("user", [
      "MANAGE_USERS",
      "MANAGE_ROLES",
      "MANAGE_DEPARTMENTS",
      "VIEW_DEPARTMENTS",
      "VIEW_ROLES_PERMISSIONS",
      "VIEW_INTERNAL_USERS",
    ]);
  }

  get canSeeIntegrationsNav(): boolean {
    return this.canSeeModule("integrations", [
      "MANAGE_INTEGRATIONS",
      "VIEW_INTEGRATIONS",
      "VIEW_VENDOR_LOGS",
      "VIEW_VENDOR_CONFIGURATION",
      "VIEW_VENDOR_API",
      "VIEW_VENDOR_API_LOGS",
      "VIEW_SOCIAL_HUB",
      "MANAGE_SOCIAL_HUB",
    ]);
  }

  get canSeeSocialHubNav(): boolean {
    return this.authService.hasAnyPermission([
      "VIEW_SOCIAL_HUB",
      "MANAGE_SOCIAL_HUB",
    ]);
  }

  get canManageIntegrationsNav(): boolean {
    return this.authService.hasAnyPermission([
      "MANAGE_INTEGRATIONS",
      "VIEW_VENDOR_CONFIGURATION",
      "VIEW_VENDOR_API",
    ]);
  }

  get canViewVendorLogsNav(): boolean {
    return this.authService.hasAnyPermission([
      "MANAGE_INTEGRATIONS",
      "VIEW_VENDOR_LOGS",
      "VIEW_VENDOR_API_LOGS",
    ]);
  }

  get canSeeRulesNav(): boolean {
    return (
      this.canSeeModule("rules-and-automations", [
        "MANAGE_USERS",
        "VIEW_APPROVAL_RULES",
        "VIEW_ASSIGNMENT_RULES",
        "VIEW_CP_GENERIC_MASTERS",
      ]) ||
      this.canSeeModule("scoring-rules", ["VIEW_SCORING_RULES", "MANAGE_USERS"])
    );
  }

  get canSeeSettingsNav(): boolean {
    return this.canSeeModule("settings", ["VIEW_SETTINGS", "EDIT_SETTINGS"]);
  }

  get canSeeSystemNav(): boolean {
    return (
      this.canSeeModule("companies", ["VIEW_COMPANIES", "MANAGE_COMPANIES"]) ||
      this.canSeeModule("settings", ["VIEW_SETTINGS", "EDIT_SETTINGS"]) ||
      this.canSeeModule("audit-logs", ["VIEW_AUDIT_LOGS"]) ||
      this.authService.hasPermission("cp_audit.view") ||
      this.canSeeModule("system", ["VIEW_RECYCLE_BIN"])
    );
  }

  private canSeeModule(slug: string, permissions: string[]): boolean {
    if (!this.authService.hasFullPortalAccess()) {
      return this.authService.hasAnyPermission(permissions);
    }

    const entitlementsResolved = this.sessionEntitlements.entitlementsResolved();
    const entitled = !entitlementsResolved
      ? true
      : this.sessionEntitlements.canAccessModule(slug);

    if (!entitled) {
      return false;
    }

    if (this.authService.hasAnyPermission(permissions)) {
      return true;
    }

    // Primary owner: plan entitlements can unlock modules without explicit RBAC rows.
    // Internal staff must hold at least one permission action from the role matrix.
    if (!this.authService.isPrimaryPartnerUser()) {
      return false;
    }

    return areAnyPermissionsCoveredByEntitlements(
      permissions,
      (moduleSlug) => this.sessionEntitlements.canAccessModule(moduleSlug),
      entitlementsResolved,
    );
  }

  navLockHint = "";

  onNavClick(event: Event, locked: boolean): void {
    if (locked) {
      event.preventDefault();
      event.stopPropagation();
      this.navLockHint =
        "At least one builder must approve your access request to unlock the full portal.";
      return;
    }
    this.navLockHint = "";
    this.toggleMobileMenu();
  }

  onAccordionClick(event: Event, name: string): void {
    if (this.isNavLocked) {
      event.preventDefault();
      event.stopPropagation();
      this.navLockHint =
        "At least one builder must approve your access request to unlock the full portal.";
      return;
    }
    this.navLockHint = "";
    this.toggleAccordion(name);
  }

  async initStore() {
    this.storeData
      .select((d) => d.index)
      .subscribe((d) => {
        this.store = d;
      });
  }

  ngOnInit(): void {
    this.initStore();
    this.syncAccordionFromUrl();
  }

  syncAccordionFromUrl(): void {
    const path = this.router.url.split("?")[0];
    const match = SidebarComponent.ROUTE_ACCORDION_PREFIXES.find(
      ({ prefix }) => path === prefix || path.startsWith(`${prefix}/`)
    );
    this.activeDropdown = match ? [match.accordion] : [];
  }

  toggleMobileMenu() {
    if (window.innerWidth < 1024) {
      this.storeData.dispatch({ type: "toggleSidebar" });
    }
  }

  toggleAccordion(name: string, _parent?: string) {
    if (this.activeDropdown.includes(name)) {
      this.activeDropdown = this.activeDropdown.filter((d) => d !== name);
    } else {
      this.activeDropdown = [...this.activeDropdown, name];
    }
  }
}
